import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { readNarrativeTopicSearch } from '@api/narratives';
import { checkIsTopicCardCountMax, getTopicLibraryCardIcon } from '../../utilities/helpers';
import NarrativesSearchBar from '../NarrativesSearchBar';

import {
  Badge,
  ButtonV2,
  CloseIcon,
  IconButtonV2,
  LoaderSkeleton,
  PlusPlainIcon,
  TopicLibraryCard,
  truncateTextWithEllipsis,
  useDebounce,
} from '@utilities';

import styles from './_index.module.scss';

const NarrativesTopicsWindow = ({
  activeTopicIds = [],
  availableTags = [],
  hasTagLoadError = false,
  narrativeTopicGroups = null,
  onCloseToolWindow,
  onInsertTopicCard,
  selectedTags = [],
  setSelectedTags = () => {},
}) => {
  const [searchValue, setSearchValue, { isDebouncing, signal }] = useDebounce('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchResultsQuery, setSearchResultsQuery] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearchError, setHasSearchError] = useState(false);
  const searchControllerRef = useRef(null);

  const isInitialLoading = !Array.isArray(narrativeTopicGroups);

  const activeTopicIdSet = useMemo(
    () => new Set((activeTopicIds || []).filter((id) => id != null).map(String)),
    [activeTopicIds]
  );
  const handleInsertTopic = (group) => {
    if (
      !group ||
      typeof onInsertTopicCard !== 'function' ||
      checkIsTopicCardCountMax(activeTopicIdSet.size)
    )
      return;
    onInsertTopicCard(group);
  };

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  useEffect(() => {
    if (!normalizedSearchValue) {
      searchControllerRef.current?.abort();
      setSearchResults(null);
      setSearchResultsQuery(null);
      setIsSearching(false);
      setHasSearchError(false);
      return undefined;
    }

    searchControllerRef.current?.abort();
    const abortController = new AbortController();
    searchControllerRef.current = abortController;
    setIsSearching(true);
    setHasSearchError(false);

    readNarrativeTopicSearch({
      signal: abortController.signal,
      userInput: searchValue.trim(),
    })
      .then((results) => {
        if (abortController.signal.aborted) return;
        setSearchResults(results);
        setSearchResultsQuery(normalizedSearchValue);
      })
      .catch((error) => {
        if (
          abortController.signal.aborted ||
          error?.name === 'AbortError' ||
          error?.name === 'CanceledError'
        )
          return;
        console.error('Error searching narrative topics:', error);
        setSearchResults([]);
        setSearchResultsQuery(normalizedSearchValue);
        setHasSearchError(true);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setIsSearching(false);
      });

    return () => {
      abortController.abort();
    };
  }, [signal]);

  const isSearchLoading =
    Boolean(normalizedSearchValue) &&
    (isDebouncing || isSearching || searchResultsQuery !== normalizedSearchValue);
  const trimmedSearchValue = searchValue.trim();

  const filteredNarrativeTopics = useMemo(() => {
    const groups = Array.isArray(narrativeTopicGroups) ? narrativeTopicGroups : [];
    if (!normalizedSearchValue || !Array.isArray(searchResults)) return groups;

    const groupsBySearchKey = new Map();
    groups.forEach((group) => {
      [group.grouping, ...(group.topics || []).flatMap((topic) => [topic?.id, topic?.name])]
        .filter(Boolean)
        .forEach((value) => groupsBySearchKey.set(String(value), group));
    });

    return searchResults.reduce((matchingGroups, result) => {
      const group = groupsBySearchKey.get(String(result));
      if (group && !matchingGroups.includes(group)) matchingGroups.push(group);
      return matchingGroups;
    }, []);
  }, [narrativeTopicGroups, normalizedSearchValue, searchResults]);

  const favoriteTopics = useMemo(
    () =>
      normalizedSearchValue
        ? []
        : filteredNarrativeTopics.filter((group) =>
            group.topics?.some((topic) => topic.isFavorited)
          ),
    [filteredNarrativeTopics, normalizedSearchValue]
  );

  const otherTopics = useMemo(
    () =>
      normalizedSearchValue
        ? filteredNarrativeTopics
        : filteredNarrativeTopics.filter(
            (group) => !group.topics?.some((topic) => topic.isFavorited)
          ),
    [filteredNarrativeTopics, normalizedSearchValue]
  );

  const handleToggleTag = (value) => {
    setSelectedTags((prevSelectedTags) =>
      prevSelectedTags.includes(value)
        ? prevSelectedTags.filter((tag) => tag !== value)
        : [...prevSelectedTags, value]
    );
  };

  const renderLoadingCards = (cardsToRender) =>
    Array.from({ length: cardsToRender }, (_, index) => (
      <TopicLibraryCard key={`loading-card-${index}`}>
        <LoaderSkeleton height={85}>
          <rect height="75" rx="7" ry="7" width="80px" x="0" y="0" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="5" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="25" />
        </LoaderSkeleton>
      </TopicLibraryCard>
    ));

  const renderTopicCards = (groups) =>
    groups
      .filter(
        (group) =>
          Boolean(group?.grouping) && Array.isArray(group?.topics) && group.topics.length > 0
      )
      .map((group) => {
        const topicIds = group.topics
          .map((topic) => topic?.id)
          .filter((id) => id != null)
          .map(String);
        const addedVariants = topicIds.filter((id) => activeTopicIdSet.has(id)).length;
        const totalVariants = group.topics.length;
        const isDisabled = addedVariants >= totalVariants;
        const isActive = addedVariants > 0;
        const representativeTopic = group.topics[0];

        return (
          <TopicLibraryCard
            className={
              isDisabled
                ? styles['narratives-topics-window-topic-library-card-disabled']
                : styles['narratives-topics-window-topic-library-card']
            }
            footer={
              <div className={styles['topic-card-footer']}>
                <ButtonV2
                  className={styles['narratives-topics-window-insert-button']}
                  disabled={isDisabled}
                  icon={isDisabled ? null : <PlusPlainIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleInsertTopic(group);
                  }}
                  size="small"
                  variant="subtle"
                >
                  {isDisabled ? 'All variants added' : 'Insert'}
                </ButtonV2>
                {totalVariants > 1 && (
                  <Badge
                    label={
                      addedVariants === 0
                        ? `${totalVariants} Variants`
                        : `${addedVariants}/${totalVariants} variants added`
                    }
                  />
                )}
              </div>
            }
            icon={getTopicLibraryCardIcon(representativeTopic.vizType)}
            isActive={isActive}
            isFooterAlwaysVisible
            key={group.grouping}
            title={
              truncateTextWithEllipsis({ length: 80, text: group.grouping }) || 'Untitled Topic'
            }
          />
        );
      });

  return (
    <div className={styles['topics-window']}>
      <div className={styles['topics-window-header-row']}>
        <strong className={styles['topics-window-header']}>Assemble with Topic Cards</strong>
        {onCloseToolWindow ? (
          <IconButtonV2
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            tooltip="Close"
            tooltipProps={{ placement: 'left' }}
            variant="default"
          />
        ) : null}
      </div>
      <NarrativesSearchBar
        ariaLabel="Find a topic"
        availableTags={availableTags}
        handleToggleTag={handleToggleTag}
        hasTagLoadError={hasTagLoadError}
        placeholderValue="Find a topic"
        searchValue={searchValue}
        selectedTags={selectedTags}
        setSearchValue={setSearchValue}
      />

      <div className={styles['topics-window-topic-library']}>
        {isInitialLoading || isSearchLoading ? (
          <>
            {renderLoadingCards(3)}
            {isInitialLoading && renderLoadingCards(6)}
          </>
        ) : (
          <>
            {favoriteTopics.length > 0 && (
              <>
                <h4 className={styles['topics-window-section-title']}>Favorite Topic Cards</h4>
                {renderTopicCards(favoriteTopics)}
              </>
            )}

            <h4 className={styles['topics-window-section-title']}>
              {favoriteTopics.length > 0 ? 'Other Topic Cards' : 'Topic Cards'}
            </h4>
            {renderTopicCards(otherTopics)}
          </>
        )}
        {!isInitialLoading && !isSearchLoading && filteredNarrativeTopics.length === 0 && (
          <div className={styles['topics-window-no-results']}>
            {hasSearchError ? (
              'We could not search topic cards. Please try again.'
            ) : normalizedSearchValue ? (
              <>
                <strong>No topic cards matched “{trimmedSearchValue}”.</strong>
                <br />
                Try describing the topic card or insight you’re looking for, or clear filters.
              </>
            ) : (
              'No topic cards found.'
            )}
          </div>
        )}
      </div>
    </div>
  );
};

NarrativesTopicsWindow.propTypes = {
  activeTopicIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  availableTags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  hasTagLoadError: PropTypes.bool,
  narrativeTopicGroups: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.arrayOf(
      PropTypes.shape({
        grouping: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
        topics: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            isFavorited: PropTypes.bool,
            name: PropTypes.string,
            tags: PropTypes.arrayOf(PropTypes.string),
            variant: PropTypes.object,
            vizType: PropTypes.string,
          })
        ),
      })
    ),
  ]),
  onCloseToolWindow: PropTypes.func,
  onInsertTopicCard: PropTypes.func,
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  setSelectedTags: PropTypes.func,
};

export default NarrativesTopicsWindow;
